import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { removeFromComparisonMatrix } from '../../../../store/redux/slices/offersSlice';

// Components
import { ActionButton } from 'wg-fe-ui';
import ComparisonBarVS from './ComparisonBarVS';
import ComparisonBarItem from './ComparisonBarItem';
import ComparisonItemEmpty from './ComparisonItemEmpty';

// Types
import { RootState } from '../../../../store/redux/reducers/rootReducer';
import { useIsFetching } from 'react-query';
import useSessionId from '../../../../../../flows/src/hooks/useSessionId';
import { Box, Center, HStack } from '@wegroup/design-system';

interface Props {
  riskObject: string;
  setIsComparisonActive: (arg0: boolean) => void;
}

const ComparisonBar: React.FC<React.PropsWithChildren<Props>> = ({
  riskObject,
  setIsComparisonActive,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const comparisonMatrixOffers = useSelector(
    (state: RootState) => state.offers.comparisonMatrixOffers,
  );

  const sessionId = useSessionId();

  const isFetchingProducts = useIsFetching([sessionId, 'products', riskObject]);

  return (
    <HStack
      w="100%"
      boxShadow="level1"
      p="6"
      spacing="6"
      bg="white"
      align="stretch"
    >
      <HStack
        divider={
          <Box px="3" display={{ base: 'none', laptopL: 'block' }}>
            <ComparisonBarVS />
          </Box>
        }
        w="100%"
        display={{ base: 'grid', laptopL: 'flex' }}
        gridTemplateColumns="repeat(3, 1fr)"
        gap={{ base: '4', laptopL: 'unset' }}
      >
        {comparisonMatrixOffers[riskObject]?.map((offer) => (
          <ComparisonBarItem
            key={offer.id}
            offer={offer}
            onRemove={() =>
              dispatch(
                removeFromComparisonMatrix({ riskObject, id: offer.id || '' }),
              )
            }
          />
        ))}
        {(comparisonMatrixOffers[riskObject]?.length || 0) < 5 && (
          <ComparisonItemEmpty />
        )}
      </HStack>
      <Center>
        <ActionButton
          onClick={() => setIsComparisonActive(true)}
          disabled={
            !!(
              (comparisonMatrixOffers[riskObject]?.length || 0) < 2 ||
              isFetchingProducts
            )
          }
        >
          {t('Compare')}
        </ActionButton>
      </Center>
    </HStack>
  );
};

export default ComparisonBar;
